import type { ProductListItem } from '@travel-planner/shared/schemas';
import type { PlannerDynamicPrice } from './advisor-price';
import type { AdvisorProduct, AdvisorRegion } from './types';
import { resolveProductTitle } from '@products/entities/product/lib/title';
import { resolveThumbnailUrl } from '@travel-planner/shared/utils';
import { advisorChildPrice, advisorClientPrice } from './advisor-price';
import { strapline } from './card-facts';
import { kindFromCategory } from './catalog';
import { hotelStars } from './hotel-class';
import {
  chapterRegionOf,
  regionAtPoint,
  regionForCity,
} from './region-at-point';
import {
  productClockMinutes,
  productDurationMinutes,
  productPoint,
} from './v3-results';

export const parseProductTags = (tags: unknown): string[] => {
  if (!Array.isArray(tags)) return [];
  return tags.flatMap((tag) => {
    if (typeof tag === 'string' && tag.trim()) return [tag.trim()];
    if (
      tag &&
      typeof tag === 'object' &&
      'name' in tag &&
      typeof (tag as { name: unknown }).name === 'string'
    ) {
      const name = (tag as { name: string }).name.trim();
      return name ? [name] : [];
    }
    return [];
  });
};

/**
 * The product's chapter region — one of the country's top-level regions: the
 * one whose outline holds its map point (the filtered region first), else the
 * filtered region, else the nearest. Without a map point, the filtered region,
 * else the one region whose XT city list names its city. Never a city.
 */
export const resolveRegionForProduct = (
  product: Pick<ProductListItem, 'city' | 'country'> &
    Partial<Pick<ProductListItem, 'locationPoint'>>,
  regions: AdvisorRegion[],
  selectedRegionId: string | null
): AdvisorRegion | undefined => {
  const selected = regions.find((region) => region.id === selectedRegionId);
  const selectedChapter = selected
    ? chapterRegionOf(selected, regions)
    : undefined;
  const point = productPoint(product.locationPoint ?? null);
  if (point) {
    const holding = regionAtPoint(point, regions, selectedChapter?.id ?? null);
    if (holding && (!selectedChapter || holding.id === selectedChapter.id)) {
      return holding;
    }
    return selectedChapter ?? holding;
  }
  return selectedChapter ?? regionForCity(product.city, regions);
};

/**
 * A catalogue row, with the contract price of a dynamic product for the brief
 * when the host priced one (the agent portal, XT-951).
 */
export type PlannerListItem = ProductListItem & {
  dynamicPrice?: PlannerDynamicPrice | null;
};

export const mapListItemToAdvisorProduct = (
  item: PlannerListItem,
  regions: AdvisorRegion[],
  selectedRegionId: string | null,
  adultTravellerIds?: ReadonlySet<string>,
  childTravellerIds?: ReadonlySet<string>
): AdvisorProduct => {
  const region = resolveRegionForProduct(item, regions, selectedRegionId);
  // A dynamic product shows what the itinerary will price it, not its fixed
  // price.
  const priced = item.dynamicPrice ?? null;
  const price = priced ? priced.price : item.price;
  return {
    id: item.id,
    title: resolveProductTitle(item.title),
    subtitle: item.city,
    tags: parseProductTags(item.hashtags),
    clientPrice: advisorClientPrice(price, adultTravellerIds),
    childPrice: advisorChildPrice(price, childTravellerIds),
    currency: priced?.currency ?? item.currency,
    kind: kindFromCategory(item.contentCategory),
    regionId: region?.id ?? null,
    regionTitle: region?.title ?? '',
    thumbnail: resolveThumbnailUrl(item.thumbnail),
    location: productPoint(item.locationPoint),
    durationMinutes: productDurationMinutes(item.legs),
    pricingType: priced?.pricingType ?? item.pricingType ?? null,
    country: item.country,
    avgRoomNight: item.avgRoomNight ?? null,
    strapline: strapline(item.shortMarketingTextEn ?? null),
    startMinutes: productClockMinutes(item.legs, 'startTime'),
    endMinutes: productClockMinutes(item.legs, 'endTime'),
    facts: item.cardFacts ?? null,
    // The product's Labels (`tags`) are its booking conditions.
    conditions: parseProductTags(item.tags),
    subcategory: item.contentSubcategory ?? null,
    stars: hotelStars(item.stars),
  };
};
